import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Eye,
  Hash,
  Loader2,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Star,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../slices';
import {
  clearStarlineResultError,
  clearStarlineResultSuccess,
  declareStarlineResult,
  fetchStarlineSessions,
  fetchStarlineActualWinners,
  previewStarlineWinners,
  reopenStarlineBid,
  revertStarlineOpenBid,
} from '../../slices/starline/starlineResultSlice';

type UnknownRecord = Record<string, unknown>;

type WinnerRow = {
  id: string | number;
  user: string;
  mobile: string;
  bidType: string;
  bidNumber: string;
  bidAmount: number;
  winningAmount: number;
};

type WinnerPreviewView = {
  marketName: string;
  sessionTime: string;
  sessionDate: string;
  resultDisplay: string;
  totalWinners: number;
  totalBidAmount: number;
  totalWinningAmount: number;
  winners: WinnerRow[];
};

const today = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().split('T')[0];
};

const calculateDigit = (pana: string) => {
  if (!/^\d{3}$/.test(pana)) return '';

  return String(
    pana.split('').reduce((sum, value) => sum + Number(value), 0) % 10
  );
};

const formatDate = (value: string) => {
  if (!value) return '—';

  const date = new Date(`${value}T00:00:00`);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const formatTime = (value: string) => {
  if (!value) return '—';

  const [hours, minutes] = value.split(':').map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return value;
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTime = (value: string | null) => {
  if (!value) return '—';

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(value || 0);

const statusClasses = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'closed':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'declared':
    case 'result_declared':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    case 'settled':
      return 'border-indigo-200 bg-indigo-50 text-indigo-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
};

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' ? (value as UnknownRecord) : {};

const asString = (...values: unknown[]) => {
  const value = values.find(
    (item) => item !== undefined && item !== null && item !== ''
  );

  return value === undefined || value === null ? '' : String(value);
};

const asNumber = (...values: unknown[]) => {
  const value = values.find(
    (item) => item !== undefined && item !== null && item !== ''
  );
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeWinnerPreview = (value: unknown): WinnerPreviewView | null => {
  if (!value) return null;

  const responseRoot = asRecord(value);
  const root = Object.keys(asRecord(responseRoot.data)).length
    ? asRecord(responseRoot.data)
    : responseRoot;
  const nestedSession = asRecord(root.session);
  const rawWinners = Array.isArray(root.winners)
    ? root.winners
    : Array.isArray(root.winner_list)
      ? root.winner_list
      : Array.isArray(root.results)
        ? root.results
        : [];

  const winners = rawWinners.map((item, index): WinnerRow => {
    const winner = asRecord(item);
    const user = asRecord(winner.user);

    return {
      id: asString(
        winner.bet_id,
        winner.id,
        winner.bid_id,
        winner.user_id,
        user.id,
        index + 1
      ),
      user: asString(
        winner.user_name,
        winner.username,
        winner.name,
        user.username,
        user.name,
        winner.user_id,
        'Unknown user'
      ),
      mobile: asString(
        winner.mobile,
        winner.phone,
        winner.mobile_number,
        user.mobile,
        user.phone,
        '—'
      ),
      bidType: asString(
        winner.bet_type_name,
        winner.bet_code,
        winner.bid_type,
        winner.game_type,
        winner.type,
        winner.bet_type,
        '—'
      ),
      bidNumber: asString(
        winner.selected_number,
        winner.bid_number,
        winner.number,
        winner.bid_value,
        winner.pana,
        winner.digit,
        '—'
      ),
      bidAmount: asNumber(
        winner.bid_amount,
        winner.amount,
        winner.bet_amount,
        winner.points
      ),
      winningAmount: asNumber(
        winner.winning_amount,
        winner.estimated_payout,
        winner.win_amount,
        winner.payout,
        winner.prize_amount
      ),
    };
  });

  const totalBidAmount =
    root.total_bid_amount != null
      ? asNumber(root.total_bid_amount)
      : root.total_bet_amount != null
        ? asNumber(root.total_bet_amount)
        : root.total_amount != null
          ? asNumber(root.total_amount)
          : winners.reduce((sum, winner) => sum + winner.bidAmount, 0);

  const totalWinningAmount =
    root.total_winning_amount != null
      ? asNumber(root.total_winning_amount)
      : root.estimated_payout != null
        ? asNumber(root.estimated_payout)
        : root.total_win_amount != null
          ? asNumber(root.total_win_amount)
          : root.total_payout != null
            ? asNumber(root.total_payout)
            : winners.reduce((sum, winner) => sum + winner.winningAmount, 0);

  return {
    marketName: asString(
      root.market_name,
      root.game_name,
      nestedSession.market_name,
      nestedSession.game_name,
      'Starline'
    ),
    sessionTime: asString(
      root.session_time,
      root.time,
      nestedSession.session_time
    ),
    sessionDate: asString(
      root.session_date,
      root.date,
      nestedSession.session_date
    ),
    resultDisplay:
      asString(
        root.result_display,
        nestedSession.result_display,
        root.result
      ) ||
      (
        asString(root.pana) && asString(root.digit)
          ? `${asString(root.pana)}-${asString(root.digit)}`
          : ''
      ) ||
      `${asString(root.result_pana, nestedSession.result_pana, '---')}-${asString(
        root.result_digit,
        nestedSession.result_digit,
        '-'
      )}`,
    totalWinners:
      asNumber(
          root.total_winners,
          root.winning_bets,
          root.winner_count,
          root.count
      ),
    totalBidAmount,
    totalWinningAmount,
    winners,
  };
};

const StarlineDeclareResult = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    sessions,
    selectedSession: lastDeclaredSession,
    loading,
    declaring,
    previewing,
    reverting,
    winnerPreview,
    error,
    success,
  } = useSelector((state: RootState) => state.StarlineResult);

  const [resultDate, setResultDate] = useState(today());
  const [sessionId, setSessionId] = useState('');
  const [pana, setPana] = useState('');
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [selectedWinnerSession, setSelectedWinnerSession] = useState<
    number | null
  >(null);

  const digit = useMemo(() => calculateDigit(pana), [pana]);

  const selectedSession = useMemo(
    () =>
      sessions.find((session) => String(session.id) === sessionId) || null,
    [sessions, sessionId]
  );

  const selectedActionSession = useMemo(
    () =>
      sessions.find((session) => session.id === selectedWinnerSession) || null,
    [sessions, selectedWinnerSession]
  );

  const preview = useMemo(
    () => normalizeWinnerPreview(winnerPreview),
    [winnerPreview]
  );

  const isActualWinners =
    selectedActionSession?.status.toLowerCase() === 'settled';

  const declaredCount = useMemo(
    () =>
      sessions.filter((session) =>
        Boolean(session.result_pana && session.result_digit)
      ).length,
    [sessions]
  );

  const availableCount = useMemo(
    () =>
      sessions.filter(
        (session) => !session.result_pana && session.status !== 'settled'
      ).length,
    [sessions]
  );

  const loadSessions = () => {
    dispatch(
      fetchStarlineSessions({
        session_date: resultDate,
      })
    );
  };

  useEffect(() => {
    loadSessions();
    setSessionId('');
    setPana('');
    setShowWinnerModal(false);
    setShowRevertModal(false);
    setSelectedWinnerSession(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, resultDate]);

  useEffect(() => {
    if (!success) return;

    const timer = window.setTimeout(
      () => dispatch(clearStarlineResultSuccess()),
      5000
    );

    return () => window.clearTimeout(timer);
  }, [dispatch, success]);

  useEffect(() => {
    if (!error) return;

    const timer = window.setTimeout(
      () => dispatch(clearStarlineResultError()),
      7000
    );

    return () => window.clearTimeout(timer);
  }, [dispatch, error]);

  useEffect(() => {
    if (!showWinnerModal && !showRevertModal) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || reverting) return;

      setShowWinnerModal(false);
      setShowRevertModal(false);
      setSelectedWinnerSession(null);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [reverting, showRevertModal, showWinnerModal]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (!sessionId || !/^\d{3}$/.test(pana) || !digit) return;

    const response = await dispatch(
      declareStarlineResult({
        session_id: Number(sessionId),
        result_pana: pana,
        result_digit: digit,
      })
    );

    if (declareStarlineResult.fulfilled.match(response)) {
      setPana('');
      setSessionId('');

      dispatch(
        fetchStarlineSessions({
          session_date: resultDate,
        })
      );
    }
  };

  const openPredeclareWinnerPreview = async () => {
    if (!sessionId || !/^\d{3}$/.test(pana) || !digit) return;

    const selectedSessionId = Number(sessionId);
    setSelectedWinnerSession(selectedSessionId);

    const response = await dispatch(
      previewStarlineWinners({
        session_id: selectedSessionId,
        result_pana: pana,
      })
    );

    if (previewStarlineWinners.fulfilled.match(response)) {
      setShowWinnerModal(true);
    } else {
      setSelectedWinnerSession(null);
    }
  };

  const openWinnerPreview = async (selectedSessionId: number) => {
    const session = sessions.find(
      (item) => item.id === selectedSessionId
    );

    if (!session) return;

    setSelectedWinnerSession(selectedSessionId);

    if (session.status.toLowerCase() === 'settled') {
      const response = await dispatch(
        fetchStarlineActualWinners(selectedSessionId)
      );

      if (fetchStarlineActualWinners.fulfilled.match(response)) {
        setShowWinnerModal(true);
      } else {
        setSelectedWinnerSession(null);
      }
      return;
    }

    const response = await dispatch(
      previewStarlineWinners({
        session_id: selectedSessionId,
        result_pana: session.result_pana || '',
      })
    );

    if (previewStarlineWinners.fulfilled.match(response)) {
      setShowWinnerModal(true);
    } else {
      setSelectedWinnerSession(null);
    }
  };

  const openRevertConfirmation = (selectedSessionId: number) => {
    setSelectedWinnerSession(selectedSessionId);
    setShowRevertModal(true);
  };

  const handleClosedSessionBidRevert = async (
    selectedSessionId: number
  ) => {
    const confirmed = window.confirm(
      'Reopen this Starline session for betting for 10 minutes?'
    );

    if (!confirmed) return;

    const response = await dispatch(
      reopenStarlineBid({
        session_id: selectedSessionId,
        confirm: true,
      })
    );

    if (reopenStarlineBid.fulfilled.match(response)) {
      dispatch(
        fetchStarlineSessions({
          session_date: resultDate,
        })
      );
    }
  };

  const closeWinnerModal = () => {
    if (previewing) return;

    setShowWinnerModal(false);
    setSelectedWinnerSession(null);
  };

  const closeRevertModal = () => {
    if (reverting) return;

    setShowRevertModal(false);
    setSelectedWinnerSession(null);
  };

  const confirmRevert = async () => {
    if (!selectedWinnerSession) return;

  const response = await dispatch(
      revertStarlineOpenBid({
        session_id: selectedWinnerSession,
        confirm: true,
})
    );

    if (revertStarlineOpenBid.fulfilled.match(response)) {
      setShowRevertModal(false);
      setShowWinnerModal(false);
      setSelectedWinnerSession(null);

      dispatch(
        fetchStarlineSessions({
          session_date: resultDate,
        })
      );
    }
  };

  const valid =
    Boolean(sessionId) && /^\d{3}$/.test(pana) && Boolean(digit);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-r from-slate-950 via-violet-950 to-fuchsia-900 p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm">
                <ShieldCheck className="h-4 w-4" />
                Result Management
              </div>

              <h1 className="text-3xl font-black sm:text-4xl">
                Starline Declare Result
              </h1>

              <p className="mt-2 max-w-2xl text-sm text-white/75 sm:text-base">
                Select a Starline game session, enter the winning panna and
                declare its calculated digit safely.
              </p>
            </div>

            <button
              type="button"
              onClick={loadSessions}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 font-bold transition hover:bg-white/20 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`}
              />
              Refresh sessions
            </button>
          </div>

          <div className="mt-7 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                Total Sessions
              </p>
              <p className="mt-1 text-3xl font-black">{sessions.length}</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                Available
              </p>
              <p className="mt-1 text-3xl font-black">{availableCount}</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                Declared
              </p>
              <p className="mt-1 text-3xl font-black">{declaredCount}</p>
            </div>
          </div>
        </section>

        {success && (
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-bold">Success</p>
              <p className="text-sm">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-bold">Unable to complete request</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.35fr_0.65fr]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-violet-100 p-3 text-violet-700">
                <Star className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">
                  Declare Starline Result
                </h2>
                <p className="text-sm text-slate-500">
                  Select a date and game session before entering the panna.
                </p>
              </div>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <label>
                  <span className="mb-2 block text-sm font-semibold text-slate-700">
                    Result Date
                  </span>
                  <div className="relative">
                    <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      type="date"
                      value={resultDate}
                      onChange={(event) => setResultDate(event.target.value)}
                      className="w-full rounded-xl border border-slate-300 py-3 pl-12 pr-4 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    />
                  </div>
                </label>

                <label>
                  <span className="mb-2 block text-sm font-semibold text-slate-700">
                    Game Name
                  </span>
                  <select
                    value={sessionId}
                    onChange={(event) => setSessionId(event.target.value)}
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:bg-slate-100"
                  >
                    <option value="">
                      {loading
                        ? 'Loading sessions...'
                        : 'Select game session'}
                    </option>

                    {sessions.map((session) => (
                      <option
                        key={session.id}
                        value={session.id}
                        disabled={Boolean(session.result_pana)}
                      >
                        {session.market_name} ·{' '}
                        {formatTime(session.session_time)}
                        {session.result_display
                          ? ` · Declared ${session.result_display}`
                          : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
                <label>
                  <span className="mb-2 block text-sm font-semibold text-slate-700">
                    Select Panna
                  </span>
                  <div className="relative">
                    <Hash className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={pana}
                      onChange={(event) =>
                        setPana(
                          event.target.value.replace(/\D/g, '').slice(0, 3)
                        )
                      }
                      placeholder="Example: 123"
                      className="w-full rounded-xl border border-slate-300 py-3 pl-12 pr-4 text-lg font-bold outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    />
                  </div>
                </label>

                <div>
                  <span className="mb-2 block text-sm font-semibold text-slate-700">
                    Digit
                  </span>
                  <div className="flex h-[52px] items-center justify-center rounded-xl border border-violet-200 bg-violet-50 text-2xl font-black text-violet-700">
                    {digit || '—'}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4 text-sm">
                <div className="grid gap-3 sm:grid-cols-3">
                  <p>
                    <span className="text-slate-500">Date:</span>{' '}
                    <strong>{formatDate(resultDate)}</strong>
                  </p>
                  <p>
                    <span className="text-slate-500">Game:</span>{' '}
                    <strong>
                      {selectedSession
                        ? formatTime(selectedSession.session_time)
                        : 'Not selected'}
                    </strong>
                  </p>
                  <p>
                    <span className="text-slate-500">Result:</span>{' '}
                    <strong>
                      {pana || '---'}-{digit || '-'}
                    </strong>
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={openPredeclareWinnerPreview}
                  disabled={!valid || previewing || declaring}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-5 py-3.5 font-bold text-violet-700 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {previewing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Eye className="h-5 w-5" />
                      Show Winner&apos;s
                    </>
                  )}
                </button>

                <button
                  type="submit"
                  disabled={!valid || declaring}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-700 px-5 py-3.5 font-bold text-white transition hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {declaring ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Declaring...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-5 w-5" />
                      Declare Starline Result
                    </>
                  )}
                </button>
              </div>
            </form>
          </section>

          <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-lg font-black text-slate-900">
              Declaration Status
            </h2>

            {lastDeclaredSession ? (
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-600">
                    Last Declared
                  </p>
                  <p className="mt-2 text-3xl font-black text-emerald-900">
                    {lastDeclaredSession.result_display || '—'}
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl bg-slate-50 p-4 text-sm">
                  <p className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Market</span>
                    <strong>{lastDeclaredSession.market_name}</strong>
                  </p>
                  <p className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Session</span>
                    <strong>
                      {formatTime(lastDeclaredSession.session_time)}
                    </strong>
                  </p>
                  <p className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Declared at</span>
                    <strong className="text-right">
                      {formatDateTime(
                        lastDeclaredSession.result_declared_at
                      )}
                    </strong>
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                <Clock3 className="mx-auto h-9 w-9 text-slate-300" />
                <p className="mt-3 font-bold text-slate-700">
                  No result declared yet
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  The most recent declaration will appear here.
                </p>
              </div>
            )}
          </aside>
        </div>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <h2 className="text-xl font-black text-slate-900">
                Starline Sessions
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Sessions for {formatDate(resultDate)}
              </p>
            </div>

            <span className="rounded-full bg-violet-50 px-3 py-1 text-sm font-bold text-violet-700">
              {sessions.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-4">Game</th>
                  <th className="px-5 py-4">Session Time</th>
                  <th className="px-5 py-4">Result</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Declared At</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <Loader2 className="mx-auto h-7 w-7 animate-spin text-violet-600" />
                      <p className="mt-3 text-sm text-slate-500">
                        Loading Starline sessions...
                      </p>
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-12 text-center text-sm text-slate-500"
                    >
                      No Starline sessions found for this date.
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => {
                    const hasDeclaredResult = Boolean(
                      session.result_pana && session.result_digit
                    );
                    const isSettled =
                      session.status.toLowerCase() === 'settled';
                    const isClosed =
                      session.status.toLowerCase() === 'closed';
                    const isPreviewingThisSession =
                      previewing &&
                      selectedWinnerSession === session.id;

                    return (
                      <tr key={session.id} className="hover:bg-slate-50">
                        <td className="px-5 py-4">
                          <p className="font-bold text-slate-900">
                            {session.market_name}
                          </p>
                          <p className="text-xs text-slate-400">
                            Session #{session.id}
                          </p>
                        </td>

                        <td className="px-5 py-4 font-semibold text-slate-700">
                          {formatTime(session.session_time)}
                        </td>

                        <td className="px-5 py-4">
                          <span className="font-black text-violet-700">
                            {session.result_display || '—'}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold uppercase ${statusClasses(
                              session.status
                            )}`}
                          >
                            {session.status.replace(/_/g, ' ')}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-sm text-slate-600">
                          {formatDateTime(session.result_declared_at)}
                        </td>

                        <td className="px-5 py-4">
                          {hasDeclaredResult ? (
                            <div className="flex min-w-max justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  openWinnerPreview(session.id)
                                }
                                disabled={
                                  previewing || reverting || !hasDeclaredResult
                                }
                                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {isPreviewingThisSession ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                                Show Winner&apos;s
                              </button>

                              {!isSettled && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openRevertConfirmation(session.id)
                                  }
                                  disabled={previewing || reverting}
                                  className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  Open Bid Revert
                                </button>
                              )}
                            </div>
                          ) : isClosed ? (
                            <div className="flex min-w-max justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleClosedSessionBidRevert(session.id)
                                }
                                disabled={reverting}
                                className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {reverting ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="h-4 w-4" />
                                )}
                                Bid Revert
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setSessionId(String(session.id));
                                  setPana('');
                                  window.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                  });
                                }}
                                disabled={reverting}
                                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <ShieldCheck className="h-4 w-4" />
                                Declare Result
                              </button>
                            </div>
                          ) : (
                            <p className="text-right text-xs font-semibold text-slate-400">
                              No actions
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {showWinnerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="winner-preview-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeWinnerModal();
          }}
        >
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-violet-950 to-fuchsia-900 p-5 text-white sm:p-6">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                  <Users className="h-4 w-4" />
                  {isActualWinners ? 'Actual Winners' : 'Potential Winners'}
                </div>
                <h2
                  id="winner-preview-title"
                  className="text-2xl font-black"
                >
                  {isActualWinners ? 'Actual Settled Winners' : 'Potential Winners Preview'}
                </h2>
                <p className="mt-1 text-sm text-white/70">
                  {isActualWinners
                    ? 'View recorded winning bids and payouts after settlement.'
                    : 'Review estimated winning bids and payouts before settlement.'}
                </p>
              </div>

              <button
                type="button"
                onClick={closeWinnerModal}
                disabled={previewing}
                aria-label="Close winner preview"
                className="rounded-xl border border-white/20 bg-white/10 p-2 transition hover:bg-white/20 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {previewing ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center p-10 text-slate-500">
                <Loader2 className="h-10 w-10 animate-spin text-violet-600" />
                <p className="mt-4 font-semibold">
                  Calculating Starline winners...
                </p>
              </div>
            ) : preview ? (
              <>
                <div className="grid gap-3 border-b border-slate-200 bg-slate-50 p-5 sm:grid-cols-2 lg:grid-cols-4 sm:p-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Game
                    </p>
                    <p className="mt-1 font-black text-slate-900">
                      {preview.marketName}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      {preview.sessionDate
                        ? formatDate(preview.sessionDate)
                        : formatDate(resultDate)}
                      {' · '}
                      {preview.sessionTime
                        ? formatTime(preview.sessionTime)
                        : selectedActionSession
                          ? formatTime(selectedActionSession.session_time)
                          : '—'}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-violet-500">
                      {isActualWinners ? 'Declared Result' : 'Preview Result'}
                    </p>
                    <p className="mt-1 text-2xl font-black text-violet-800">
                      {preview.resultDisplay}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-500">
                      Total Winners
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-800">
                      {preview.totalWinners}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-600">
                      <Wallet className="h-4 w-4" />
                      {isActualWinners ? 'Actual Total Winning' : 'Estimated Total Winning'}
                    </p>
                    <p className="mt-1 text-2xl font-black text-emerald-800">
                      {formatCurrency(preview.totalWinningAmount)}
                    </p>
                  </div>
                </div>

                <div className="flex-1 overflow-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="sticky top-0 bg-slate-100 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-5 py-4">Winner</th>
                        <th className="px-5 py-4">Mobile</th>
                        <th className="px-5 py-4">Bid Type</th>
                        <th className="px-5 py-4">Bid Number</th>
                        <th className="px-5 py-4 text-right">Bid Amount</th>
                        <th className="px-5 py-4 text-right">
                          {isActualWinners ? 'Actual Winning Amount' : 'Estimated Winning Amount'}
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 bg-white">
                      {preview.winners.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-5 py-16 text-center"
                          >
                            <Users className="mx-auto h-10 w-10 text-slate-300" />
                            <p className="mt-3 font-bold text-slate-700">
                              No winners found
                            </p>
                            <p className="mt-1 text-sm text-slate-500">
                              No matching winning bids were found for this
                              result.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        preview.winners.map((winner) => (
                          <tr key={winner.id} className="hover:bg-slate-50">
                            <td className="px-5 py-4">
                              <p className="font-bold text-slate-900">
                                {winner.user}
                              </p>
                              <p className="text-xs text-slate-400">
                                ID: {winner.id}
                              </p>
                            </td>
                            <td className="px-5 py-4 text-sm text-slate-600">
                              {winner.mobile}
                            </td>
                            <td className="px-5 py-4">
                              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase text-slate-700">
                                {winner.bidType}
                              </span>
                            </td>
                            <td className="px-5 py-4 font-black text-violet-700">
                              {winner.bidNumber}
                            </td>
                            <td className="px-5 py-4 text-right font-semibold text-slate-700">
                              {formatCurrency(winner.bidAmount)}
                            </td>
                            <td className="px-5 py-4 text-right font-black text-emerald-700">
                              {formatCurrency(winner.winningAmount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {preview.winners.length > 0 && (
                      <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                        <tr>
                          <td
                            colSpan={4}
                            className="px-5 py-4 text-right font-black text-slate-700"
                          >
                            Totals
                          </td>
                          <td className="px-5 py-4 text-right font-black text-slate-900">
                            {formatCurrency(preview.totalBidAmount)}
                          </td>
                          <td className="px-5 py-4 text-right font-black text-emerald-700">
                            {formatCurrency(preview.totalWinningAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                <div className="flex justify-end border-t border-slate-200 bg-white p-5 sm:p-6">
                  <button
                    type="button"
                    onClick={closeWinnerModal}
                    className="rounded-xl bg-slate-900 px-5 py-3 font-bold text-white transition hover:bg-slate-800"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <div className="flex min-h-[320px] flex-col items-center justify-center p-10 text-center">
                <AlertCircle className="h-10 w-10 text-amber-500" />
                <p className="mt-3 font-bold text-slate-800">
                  Winner preview is unavailable
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Close this dialog and try the request again.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {showRevertModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revert-confirmation-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeRevertModal();
          }}
        >
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-amber-200 bg-amber-50 p-5 sm:p-6">
              <div className="flex gap-3">
                <div className="rounded-2xl bg-amber-100 p-3 text-amber-700">
                  <RotateCcw className="h-6 w-6" />
                </div>
                <div>
                  <h2
                    id="revert-confirmation-title"
                    className="text-xl font-black text-slate-900"
                  >
                    Revert Open Bid?
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    This will remove the declared result and reopen the session
                    for bidding.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeRevertModal}
                disabled={reverting}
                aria-label="Close revert confirmation"
                className="rounded-xl p-2 text-slate-500 transition hover:bg-white disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5 sm:p-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-slate-500">Game</span>
                  <strong className="text-right text-slate-900">
                    {selectedActionSession?.market_name || 'Starline'}
                  </strong>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <span className="text-slate-500">Session</span>
                  <strong className="text-right text-slate-900">
                    {selectedActionSession
                      ? formatTime(selectedActionSession.session_time)
                      : '—'}
                  </strong>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <span className="text-slate-500">Current Result</span>
                  <strong className="text-right text-violet-700">
                    {selectedActionSession?.result_display || '—'}
                  </strong>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <p>
                  Only a declared, unsettled result can be reverted. A settled
                  session will not be changed.
                </p>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 p-5 sm:flex-row sm:justify-end sm:p-6">
              <button
                type="button"
                onClick={closeRevertModal}
                disabled={reverting}
                className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-bold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmRevert}
                disabled={reverting || !selectedWinnerSession}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-3 font-bold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {reverting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Reverting...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-5 w-5" />
                    Confirm Revert
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StarlineDeclareResult;
